import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Camera, Info, Loader2, Lock, Pencil, Trash2, X } from 'lucide-react';

import { ApiError } from '@/lib/api';
import {
  TICKET_CATEGORIES,
  type TicketSummary,
  deleteTicket,
  formatAmount,
  getFolder,
  ticketDownloadUrl,
  updateTicket,
  uploadTicket,
} from '@/lib/tickets-api';
import {
  CURRENCY_SYMBOLS,
  SUPPORTED_CURRENCIES,
  ocrTicket,
  type SupportedCurrency,
} from '@/lib/ticket-ocr';
// El motor real (Google Cloud Vision) corre en el backend; aquí solo activamos
// la llamada. Si el backend no tiene credenciales, devolverá `enabled: false`
// y el modal se quedará vacío sin ruido.
const OCR_ENABLED = true;
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

/**
 * Detalle de una carpeta de tickets (vista del empleado).
 *
 * Flujo de añadir ticket:
 *  1. Pulsar "Tomar foto" abre la cámara nativa del móvil (input capture).
 *  2. Al recibir el File, se pasa a Tesseract para sugerir fecha / importe.
 *  3. El empleado revisa, edita y confirma. El POST sube el archivo + datos.
 *
 * El OCR es opcional: si Tesseract falla o se queda muy largo, el formulario
 * se abre vacío y el empleado teclea los datos a mano.
 */
export function TicketFolder() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [pending, setPending] = useState<PendingTicket | null>(null);
  const [editing, setEditing] = useState<TicketSummary | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const folderQuery = useQuery({
    queryKey: ['tickets', 'folder', id],
    queryFn: () => getFolder(id!),
    enabled: !!id,
  });


  const removeTicket = useMutation({
    mutationFn: (ticketId: string) => deleteTicket(ticketId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tickets', 'folder', id] });
      void qc.invalidateQueries({ queryKey: ['tickets', 'folders'] });
    },
  });

  if (!id) return null;
  if (folderQuery.isLoading) return <p className="p-6 text-sm text-muted-foreground">Cargando…</p>;
  if (folderQuery.error || !folderQuery.data) {
    return (
      <p role="alert" className="p-6 text-sm text-destructive">
        {folderQuery.error instanceof ApiError
          ? folderQuery.error.message
          : 'Error cargando la carpeta'}
      </p>
    );
  }

  const folder = folderQuery.data;
  const isOpen = folder.status === 'OPEN';

  // El modal permanece abierto hasta que el usuario lo cierre o suba el ticket.
  // El OCR corre fire-and-forget: si falla, el modal lo refleja con un cartel
  // discreto (deshabilitado vs no se pudo leer) para que el empleado sepa por
  // qué los campos están vacíos.
  const handleFile = (file: File) => {
    setPending({
      file,
      category: '',
      title: '',
      date: '',
      amount: '',
      currency: 'EUR',
      notes: '',
      ocrText: '',
      ocrProgress: 0,
      ocrRunning: OCR_ENABLED,
      ocrOutcome: OCR_ENABLED ? 'pending' : 'disabled',
    });
    if (OCR_ENABLED) void runOcr(file);
  };

  const runOcr = async (file: File) => {
    try {
      const result = await ocrTicket(file);
      // El backend distingue tres salidas: deshabilitado por config, fallo en
      // runtime, o éxito. Mapeamos a `ocrOutcome` para el banner del modal.
      const outcome: OcrOutcome = !result.enabled ? 'disabled' : result.failed ? 'failed' : 'ok';
      setPending((prev) =>
        prev
          ? {
              ...prev,
              ocrRunning: false,
              ocrOutcome: outcome,
              category: prev.category || result.suggestedCategory || '',
              title: prev.title || result.suggestedTitle || '',
              date: result.suggestedDate ?? '',
              amount:
                result.suggestedAmountCents != null
                  ? (result.suggestedAmountCents / 100).toFixed(2)
                  : '',
              // Si el OCR no detecta moneda, mantenemos la que ya hubiera (EUR
              // por defecto). El empleado puede cambiarla con el selector.
              currency: result.suggestedCurrency ?? prev.currency,
              ocrText: result.text,
            }
          : prev,
      );
    } catch (err) {
      // Error de red/HTTP no controlado por el backend: lo tratamos como fallo
      // del motor a efectos de cartel — la acción del empleado es la misma.
      console.warn('OCR falló, continúa manual:', err);
      setPending((prev) =>
        prev ? { ...prev, ocrRunning: false, ocrOutcome: 'failed' } : prev,
      );
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <Link
        to="/tickets"
        className="inline-flex items-center gap-1 text-[12.5px] text-muted-foreground hover:text-foreground mb-3"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Volver
      </Link>

      <header className="bg-card border border-border rounded-sm p-4 md:p-5 mb-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h1 className="text-lg md:text-xl font-semibold tracking-tightish">{folder.name}</h1>
          <span
            className={cn(
              'font-mono text-[10px] font-medium uppercase border rounded-[3px] px-2 py-0.5',
              isOpen
                ? 'bg-success/10 border-success/40 text-success'
                : 'bg-muted border-border text-muted-foreground',
            )}
            style={{ letterSpacing: '0.06em' }}
          >
            {isOpen ? 'En curso' : 'Cerrada'}
          </span>
        </div>
        {folder.description ? (
          <p className="text-[13px] text-muted-foreground mb-2">{folder.description}</p>
        ) : null}
        <p className="text-[12px] text-muted-foreground">
          {folder.ticketCount} ticket{folder.ticketCount === 1 ? '' : 's'}
          {folder.totalCents > 0 ? ` · Total ${formatAmount(folder.totalCents)}` : ''}
          {folder.tripStartDate ? ` · ${folder.tripStartDate}` : ''}
          {folder.tripEndDate && folder.tripEndDate !== folder.tripStartDate
            ? ` → ${folder.tripEndDate}`
            : ''}
        </p>
        {!isOpen && folder.closedAt ? (
          <p className="text-[12px] text-muted-foreground mt-2">
            Cerrada el {new Date(folder.closedAt).toLocaleString()} por {folder.closedByName ?? '?'}.
          </p>
        ) : null}
      </header>

      {/* Botón principal: tomar foto (solo si carpeta OPEN) */}
      {isOpen ? (
        <div className="mb-5">
          {/* IMPORTANTE: NO usamos `capture="environment"`. Chrome Android mata
              la pestaña al abrir la cámara desde el input (el page state se pierde).
              El empleado toma fotos con la app de cámara nativa y luego las añade
              desde aquí (selector de galería). Flujo más fiable y permite añadir
              fotos en bloque. */}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              // Limpiamos en el siguiente tick para permitir re-seleccionar la
              // misma foto sin que el navegador filtre el onChange duplicado.
              setTimeout(() => {
                if (e.target) e.target.value = '';
              }, 0);
            }}
          />
          <Button
            variant="primary"
            className="w-full h-14 text-base"
            onClick={() => fileRef.current?.click()}
          >
            <Camera className="h-5 w-5" />
            Añadir foto del ticket
          </Button>
          <p className="text-[11.5px] text-muted-foreground text-center mt-2 px-2">
            Haz la foto con tu cámara y selecciónala desde aquí. Puedes hacer
            varias seguidas y subirlas una a una.
          </p>
        </div>
      ) : (
        <div className="mb-5 bg-muted/30 border border-border rounded-sm p-3 text-[13px] text-muted-foreground flex items-center gap-2">
          <Lock className="h-4 w-4" />
          Carpeta cerrada. Pide a administración que la reabra si necesitas modificarla.
        </div>
      )}

      {/* Lista de tickets */}
      {folder.tickets.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">
          Aún no has añadido ningún ticket.
        </p>
      ) : (
        <ul className="space-y-2.5">
          {folder.tickets.map((t) => (
            <li
              key={t.id}
              className="bg-card border border-border rounded-sm p-3 flex items-center gap-3"
            >
              <img
                src={ticketDownloadUrl(t.id)}
                alt={t.title}
                className="h-14 w-14 object-cover rounded-sm border border-border flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  {t.category ? (
                    <span
                      className="inline-flex items-center font-mono text-[9px] font-medium uppercase border border-icaria-600/40 bg-icaria-50 text-icaria-700 rounded-[3px] px-1.5 py-0.5"
                      style={{ letterSpacing: '0.06em' }}
                    >
                      {t.category}
                    </span>
                  ) : null}
                  <p className="font-medium text-[14px] truncate">{t.title}</p>
                </div>
                <p className="text-[12px] text-muted-foreground truncate">
                  {t.ticketDate ?? 'sin fecha'} · {formatAmount(t.amountCents, t.currency)}
                </p>
              </div>
              {isOpen ? (
                <div className="flex items-center gap-1">
                  <button
                    className="p-2 text-muted-foreground hover:text-foreground rounded-sm hover:bg-muted/50"
                    onClick={() => setEditing(t)}
                    aria-label="Editar"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    className="p-2 text-muted-foreground hover:text-destructive rounded-sm hover:bg-muted/50"
                    onClick={() => {
                      if (confirm(`¿Borrar "${t.title}"?`)) removeTicket.mutate(t.id);
                    }}
                    aria-label="Borrar"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {pending ? (
        <NewTicketModal
          folderId={folder.id}
          pending={pending}
          onChange={setPending}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['tickets', 'folder', folder.id] });
            void qc.invalidateQueries({ queryKey: ['tickets', 'folders'] });
            setPending(null);
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}

      {editing ? (
        <EditTicketModal
          ticket={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['tickets', 'folder', folder.id] });
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}

type OcrOutcome =
  | 'pending' // OCR en curso
  | 'ok' // OCR terminó (con o sin sugerencias útiles) — sin aviso
  | 'disabled' // backend sin credenciales configuradas
  | 'failed'; // motor OCR falló en runtime (clave inválida, red, quota…)

interface PendingTicket {
  file: File;
  category: string;
  title: string;
  date: string;
  amount: string;
  currency: SupportedCurrency;
  notes: string;
  ocrText: string;
  ocrProgress: number;
  ocrRunning: boolean;
  ocrOutcome: OcrOutcome;
}

function NewTicketModal({
  folderId,
  pending,
  onChange,
  onSaved,
  onCancel,
}: {
  folderId: string;
  pending: PendingTicket;
  onChange: (p: PendingTicket) => void;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const previewUrl = useObjectUrl(pending.file);
  const save = useMutation({
    mutationFn: () => {
      const cents = parseAmount(pending.amount);
      return uploadTicket(folderId, {
        category: pending.category || undefined,
        title: pending.title.trim() || 'Ticket sin nombre',
        ticketDate: pending.date || undefined,
        amountCents: cents,
        currency: pending.currency,
        notes: pending.notes || undefined,
        ocrText: pending.ocrText || undefined,
        file: pending.file,
      });
    },
    onSuccess: () => onSaved(),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
      <div className="w-full max-w-md bg-card border border-border rounded-sm shadow-xl max-h-[95dvh] overflow-y-auto">
        <header className="px-5 py-3 border-b border-border flex items-center justify-between sticky top-0 bg-card">
          <h2 className="text-base font-semibold">Nuevo ticket</h2>
          <button onClick={onCancel} aria-label="Cancelar" className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-5 space-y-3">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Vista previa"
              className="w-full max-h-64 object-contain bg-muted/30 rounded-sm border border-border"
            />
          ) : null}

          <OcrStatusBanner pending={pending} />

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-category">Categoría</Label>
              <CategorySelect
                id="new-category"
                value={pending.category}
                onChange={(v) => onChange({ ...pending, category: v })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-title">Nombre</Label>
              <Input
                id="new-title"
                placeholder="El Corte Inglés…"
                value={pending.title}
                onChange={(e) => onChange({ ...pending, title: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-date">Fecha</Label>
              <Input
                id="new-date"
                type="date"
                value={pending.date}
                onChange={(e) => onChange({ ...pending, date: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-amount">Importe</Label>
              <div className="flex">
                <Input
                  id="new-amount"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={pending.amount}
                  onChange={(e) => onChange({ ...pending, amount: e.target.value })}
                  className="rounded-r-none flex-1 min-w-0"
                />
                <CurrencySelect
                  value={pending.currency}
                  onChange={(c) => onChange({ ...pending, currency: c })}
                />
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-notes">Notas</Label>
            <Textarea
              id="new-notes"
              rows={2}
              value={pending.notes}
              onChange={(e) => onChange({ ...pending, notes: e.target.value })}
            />
          </div>

          {save.error ? (
            <p role="alert" className="text-sm text-destructive">
              {save.error instanceof Error ? save.error.message : 'Error guardando'}
            </p>
          ) : null}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onCancel} disabled={save.isPending}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Subiendo…' : 'Guardar ticket'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Cartel discreto que refleja el estado de la lectura automática en el modal.
 * Tres casos visibles + silencio cuando todo va bien:
 *   - pending: spinner "Leyendo ticket…"
 *   - disabled: backend sin credenciales → empleado teclea a mano
 *   - failed: motor OCR caído (clave inválida, red, quota) → lo mismo, distinto motivo
 *   - ok: ningún cartel (los campos llegan ya rellenados o vacíos si no hubo match)
 */
function OcrStatusBanner({ pending }: { pending: PendingTicket }) {
  if (pending.ocrRunning) {
    return (
      <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Leyendo ticket… {Math.round(pending.ocrProgress * 100)}%
      </div>
    );
  }
  if (pending.ocrOutcome === 'disabled') {
    return (
      <div className="flex items-start gap-2 rounded-sm border border-border bg-muted/40 px-3 py-2 text-[12.5px] text-muted-foreground">
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>Lectura automática deshabilitada — introduce los datos a mano.</span>
      </div>
    );
  }
  if (pending.ocrOutcome === 'failed') {
    return (
      <div
        role="status"
        className="flex items-start gap-2 rounded-sm border border-amber-300/60 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900"
      >
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>No pudimos leer el ticket — introduce los datos a mano.</span>
      </div>
    );
  }
  return null;
}

function EditTicketModal({
  ticket,
  onClose,
  onSaved,
}: {
  ticket: TicketSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [category, setCategory] = useState(ticket.category ?? '');
  const [title, setTitle] = useState(ticket.title);
  const [date, setDate] = useState(ticket.ticketDate ?? '');
  const [amount, setAmount] = useState(
    ticket.amountCents != null ? (ticket.amountCents / 100).toFixed(2) : '',
  );
  // Si la moneda guardada no es una de las soportadas (datos viejos),
  // por defecto la pisamos a EUR para no romper el select.
  const initialCurrency = (SUPPORTED_CURRENCIES as readonly string[]).includes(ticket.currency)
    ? (ticket.currency as SupportedCurrency)
    : 'EUR';
  const [currency, setCurrency] = useState<SupportedCurrency>(initialCurrency);
  const [notes, setNotes] = useState(ticket.notes ?? '');
  const save = useMutation({
    mutationFn: () =>
      updateTicket(ticket.id, {
        category: category.trim(),
        title: title.trim() || undefined,
        ticketDate: date || undefined,
        amountCents: parseAmount(amount),
        currency,
        notes: notes || undefined,
      }),
    onSuccess: () => onSaved(),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
      <div className="w-full max-w-md bg-card border border-border rounded-sm shadow-xl">
        <header className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-base font-semibold">Editar ticket</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-category">Categoría</Label>
              <CategorySelect id="edit-category" value={category} onChange={setCategory} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-title">Nombre</Label>
              <Input id="edit-title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-date">Fecha</Label>
              <Input id="edit-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-amount">Importe</Label>
              <div className="flex">
                <Input
                  id="edit-amount"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="rounded-r-none flex-1 min-w-0"
                />
                <CurrencySelect value={currency} onChange={setCurrency} />
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-notes">Notas</Label>
            <Textarea id="edit-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {save.error ? (
            <p role="alert" className="text-sm text-destructive">
              {save.error instanceof Error ? save.error.message : 'Error guardando'}
            </p>
          ) : null}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Selector compacto de moneda pegado al input de importe (estilo addon).
 * Cuatro opciones cerradas: EUR, USD, AED, SAR. El OCR sugiere una y el
 * empleado puede cambiarla si la detección falla.
 */
function CurrencySelect({
  value,
  onChange,
}: {
  value: SupportedCurrency;
  onChange: (v: SupportedCurrency) => void;
}) {
  return (
    <select
      aria-label="Moneda"
      value={value}
      onChange={(e) => onChange(e.target.value as SupportedCurrency)}
      className="h-9 rounded-l-none border border-l-0 border-line bg-card px-2 py-1 text-sm font-mono shadow-sm focus:outline-none focus:ring-2 focus:ring-icaria-600/40"
    >
      {SUPPORTED_CURRENCIES.map((c) => (
        <option key={c} value={c}>
          {CURRENCY_SYMBOLS[c]}
        </option>
      ))}
    </select>
  );
}

/**
 * Selector cerrado de categorías. Si la categoría guardada en BD no está en
 * el catálogo (p. ej. un valor antiguo), la añade como opción extra para no
 * perderla al editar.
 */
function CategorySelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const inCatalog = TICKET_CATEGORIES.includes(value as never);
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-9 w-full rounded-sm border border-line bg-card px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-icaria-600/40"
    >
      <option value="">—</option>
      {!inCatalog && value ? <option value={value}>{value}</option> : null}
      {TICKET_CATEGORIES.map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
    </select>
  );
}

function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const created = URL.createObjectURL(file);
    setUrl(created);
    return () => URL.revokeObjectURL(created);
  }, [file]);
  return url;
}

function parseAmount(input: string): number | undefined {
  const trimmed = input.trim().replace(',', '.');
  if (!trimmed) return undefined;
  const num = Number(trimmed);
  if (!Number.isFinite(num) || num < 0) return undefined;
  return Math.round(num * 100);
}
